import React, { useEffect, useState } from "react";
import {
    Popover,
    Typography,
    Divider,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    Box,
    Button,
    IconButton,
    Tooltip
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getAdminNotificationsList, markAdminNotificationAsRead, markAllAdminNotificationsAsRead } from "../redux/actions/commonActions";
import { timeAgo } from "../helpers/utility";
import { DoneAll, Person, Visibility } from "@mui/icons-material";

const NotificationPopover = ({ anchorElement, onClose, onNotificationItemClick, onViewProfile }) => {
    const dispatch = useDispatch();
    const open = Boolean(anchorElement);

    const adminNotificationsResponse = useSelector((state) => state.common.adminNotifications);
    const adminNotifications = adminNotificationsResponse?.data;

    const totalRecords = adminNotificationsResponse?.metadata?.totalRecords;
    const totalPages = adminNotificationsResponse?.metadata?.totalPages;

    const loading = useSelector((state) => state.loader.loading);
    const userDetails = useSelector((state) => state.user.userDetails);

    const [queryParams, setQueryParams] = useState({
        pageNumber: 0,
        searchText: ''
    });

    useEffect(() => {
        if (open) {
            dispatch(getAdminNotificationsList(queryParams));
        }
    }, [open, dispatch, queryParams]);

    const handleNotificationItemClick = (notItem) => {
        onNotificationItemClick(notItem);
        onClose();
        handleMarkAsRead(notItem.adminUserNotificationId);
    }

    const handleMarkAllAsReadButton = () => {
        dispatch(markAllAdminNotificationsAsRead(userDetails?.adminUserId));
    }

    const handleViewResume = (notItem) => {
        window.open(notItem.actionData.resumeLink, "_blank");
    }

    const handleViewCandidateProfile = (notItem) => {
        onViewProfile(notItem);
    }

    const handleMarkAsRead = (adminUserNotificationId) => {
        dispatch(markAdminNotificationAsRead(adminUserNotificationId));
    }

    const handleScroll = (e) => {
        const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 1;
        if (bottom && !loading && adminNotifications.length < totalRecords) {
            setQueryParams((prevParams) => ({ ...prevParams, pageNumber: prevParams.pageNumber + 1 }));
        }
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorElement}
            onClose={onClose}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
            }}
            transformOrigin={{
                vertical: "top",
                horizontal: "right",
            }}
            slotProps={{
                paper: {
                    sx: { width: 350, maxHeight: 400 },
                },
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{ p: 1, fontWeight: 'bold' }}>
                    Notifications ({totalRecords})
                </Typography>
                <Button size="small" sx={{ textTransform: 'non' }} onClick={handleMarkAllAsReadButton}>Mark all as read</Button>
            </div>
            <Divider />
            <Box
                sx={{ maxHeight: 340, overflowY: "auto" }}
                onScroll={handleScroll}
            >
                <List disablePadding>
                    {adminNotifications && adminNotifications.map((notif) => (
                        <ListItem
                            disableGutters
                            key={notif.id}
                            sx={{
                                paddingLeft: '8px',
                                paddingRight: '8px',
                                bgcolor: notif.read ? "white" : "#e8f1fa",
                                opacity: notif.read ? 0.8 : 1,
                                cursor: 'pointer',
                                '&:hover': {
                                    backgroundColor: '#d1e3f6'
                                },
                                "&:hover .notification-actions": {
                                    visibility: "visible",
                                }
                            }}
                            divider
                            dense
                            onClick={() => handleNotificationItemClick(notif)}
                        >
                            {!notif.read &&
                                <span style={{
                                    height: '8px',
                                    width: '8px',
                                    minHeight: '8px',
                                    minWidth: '8px',
                                    borderRadius: '8px',
                                    backgroundColor: '#145ea8',
                                    marginRight: '8px'
                                }} />
                            }
                            <ListItemText
                                secondaryTypographyProps={{ fontSize: '0.75rem' }}
                                primary={notif.title}
                                secondary={timeAgo(notif.createDate)}
                            />

                            <Box
                                className="notification-actions"
                                sx={{
                                    position: "absolute",
                                    right: "12px",
                                    bottom: "8px",
                                    display: "flex",
                                    gap: '8px',
                                    visibility: "hidden",
                                }}
                            >
                                {notif?.actionData?.resumeLink &&
                                    <Tooltip title="View Resume">
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            sx={{
                                                backgroundColor: 'white',
                                                '&:hover': {
                                                    backgroundColor: '#ecf3fb'
                                                }
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleViewResume(notif);
                                            }}
                                        >
                                            <Visibility fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                }

                                <Tooltip title="View Profile">
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        sx={{
                                            backgroundColor: 'white',
                                            '&:hover': {
                                                backgroundColor: '#ecf3fb'
                                            }
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewCandidateProfile(notif);
                                        }}
                                    >
                                        <Person fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Mark as Read">
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        sx={{
                                            backgroundColor: 'white',
                                            '&:hover': {
                                                backgroundColor: '#ecf3fb'
                                            }
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleMarkAsRead(notif.adminUserNotificationId);
                                        }}
                                    >
                                        <DoneAll fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                            </Box>
                        </ListItem>
                    ))}
                </List>
                {loading && (
                    <Box display="flex" justifyContent="center" py={1}>
                        <CircularProgress size={24} />
                    </Box>
                )}
            </Box>

        </Popover>
    );
};

export default NotificationPopover;
